
import React, { useState, useEffect } from 'react';
import { Users, DollarSign, Copy, TrendingUp, Award, Wallet, Building, FileText, CheckCircle2, ChevronLeft, Presentation, Calculator, Globe, Star, ArrowRight } from 'lucide-react';
import KanzleiBotDemo from './KanzleiBotDemo';
import ROICalculator from './ROICalculator';
import SalesPlaybook from './SalesPlaybook';
import InlineChat from './InlineChat';
import { N8N_CONFIG } from '../constants';

const AffiliatePortal: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'dashboard' | 'tools'>('dashboard');
  const [activeTool, setActiveTool] = useState<'kanzlei' | 'roi' | 'playbook' | null>(null);
  const [partnerId, setPartnerId] = useState<string>('');
  const [domain, setDomain] = useState('https://arkorbit.at'); // DEFAULT TO NEW DOMAIN
  const [copiedLink, setCopiedLink] = useState<string | null>(null);
  
  useEffect(() => {
      const stored = localStorage.getItem('orbit_partner_id');
      if (stored) {
          setPartnerId(stored);
      } else {
          const newId = "ARK-" + Math.random().toString(36).substring(2, 7).toUpperCase();
          localStorage.setItem('orbit_partner_id', newId);
          setPartnerId(newId);
      }
  }, []);

  const copyToClipboard = (text: string, label: string) => {
      navigator.clipboard.writeText(text);
      setCopiedLink(label);
      setTimeout(() => setCopiedLink(null), 2000);
  };

  const refLink = `${domain}?ref=${partnerId}`;
  
  const renderDashboard = () => (
      <div className="space-y-8 animate-fade-in">
          {/* HEADER CARD */}
          <div className="bg-gradient-to-r from-amber-900/40 to-slate-900 border border-amber-500/20 p-8 rounded-3xl relative overflow-hidden">
              <div className="absolute top-0 right-0 w-64 h-64 bg-amber-500/10 rounded-full blur-[80px] -mr-16 -mt-16 pointer-events-none"></div>
              <div className="relative z-10 flex justify-between items-center">
                  <div>
                      <h3 className="text-3xl font-bold text-white mb-2 tracking-tight">ARKORBIT Partner</h3>
                      <p className="text-amber-200/80 text-lg">High-Ticket Vermittlung · Österreichs Kanzlei-KI.</p>
                  </div>
                  <div className="hidden md:block">
                      <div className="bg-amber-500/10 border border-amber-500/30 px-4 py-2 rounded-full flex items-center">
                          <Star className="w-4 h-4 text-amber-400 mr-2 fill-amber-400" />
                          <span className="text-amber-300 font-bold text-sm">LEVEL: EXPERT</span>
                      </div>
                  </div>
              </div>
          </div>
          
          {/* STATS GRID */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-slate-900 border border-slate-800 p-6 rounded-2xl hover:border-slate-700 transition-colors">
                <h3 className="text-slate-500 text-xs font-bold uppercase mb-2 tracking-wider flex items-center"><Users className="w-3 h-3 mr-2" /> Leads (Active)</h3>
                <div className="text-4xl font-black text-white">3</div>
                <div className="mt-4 text-xs text-blue-400 flex items-center bg-blue-900/10 py-1 px-2 rounded w-fit">
                    <TrendingUp className="w-3 h-3 mr-1" /> 2 Audits in Prüfung
                </div>
            </div>
            <div className="bg-slate-900 border border-slate-800 p-6 rounded-2xl hover:border-slate-700 transition-colors">
                <h3 className="text-emerald-400 text-xs font-bold uppercase mb-2 tracking-wider flex items-center"><Wallet className="w-3 h-3 mr-2" /> Provision (Offen)</h3>
                <div className="text-4xl font-black text-white">—</div>
                 <div className="mt-4 text-xs text-slate-500">
                    Nächste Auszahlung: 01.03.
                </div>
            </div>
            <div className="bg-slate-900 border border-slate-800 p-6 rounded-2xl hover:border-slate-700 transition-colors">
                <h3 className="text-slate-500 text-xs font-bold uppercase mb-2 tracking-wider flex items-center"><Globe className="w-3 h-3 mr-2" /> Traffic</h3>
                <div className="text-4xl font-black text-white">124</div>
                 <div className="mt-4 text-xs text-slate-500">
                    Klicks auf Ref-Link
                </div>
            </div>
          </div>

          {/* MAIN LINK GENERATOR */}
          <div className="bg-black border border-slate-800 p-8 rounded-2xl relative overflow-hidden">
              <div className="absolute top-0 right-0 opacity-20 p-8">
                  <Globe className="w-32 h-32 text-slate-700" />
              </div>
                  <h3 className="text-lg font-bold text-white mb-4 flex items-center relative z-10">
                  <Globe className="w-5 h-5 mr-2 text-blue-500" />
                  ARKORBIT Ref-Link
              </h3>
              <div className="flex flex-col md:flex-row gap-4 mb-4 relative z-10">
                  <div className="flex-1 bg-slate-900 border border-slate-700 rounded-xl px-4 py-4 text-slate-300 font-mono text-sm flex items-center overflow-hidden">
                      <span className="truncate text-blue-400">{refLink}</span>
                  </div>
                  <button 
                    onClick={() => copyToClipboard(refLink, 'main')}
                    className="bg-blue-600 hover:bg-blue-500 text-white font-bold px-8 py-3 rounded-xl transition-all flex items-center justify-center min-w-[160px] shadow-lg shadow-blue-900/20"
                  >
                      {copiedLink === 'main' ? <CheckCircle2 className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
                      {copiedLink === 'main' ? 'Kopiert' : 'Kopieren'}
                  </button>
              </div>
              <p className="text-xs text-slate-500 relative z-10">
                  Ihr persönlicher Ref-Link für Kanzlei-Leads. 60-Tage Cookie-Tracking aktiv.
              </p>
          </div>

          {/* AFFILIATE ASSISTANT */}
          <div>
              <h3 className="text-lg font-bold text-white mb-4 flex items-center">
                  <Star className="w-5 h-5 mr-2 text-amber-400" />
                  Partner-Assistent
              </h3>
              <p className="text-slate-400 text-sm mb-4">
                  Nicht sicher wie du eine Kanzlei pitchst? Frag den Assistenten – er kennt alle Argumente und Einwände.
              </p>
              <InlineChat
                  title="PARTNER ARKO – Dein Sales-Coach"
                  subtitle="Pitch · Einwände · Formulierungen · Provision"
                  initialMessage="Hey! Ich bin PARTNER ARKO – dein persönlicher Sales-Coach. Ich gebe dir zu jeder Situation eine fertige Formulierung die du 1:1 übernehmen kannst. Frag mich z.B.: 'Ein Anwalt sagt es ist zu teuer – was sage ich?' oder 'Wie starte ich am besten ein Gespräch?'"
                  webhookUrl={N8N_CONFIG.AFFILIATE_ASSISTANT_WEBHOOK}
                  accentColor="amber"
                  mode="partner"
                  extraBody={{ sessionId: partnerId }}
                  timeoutMs={50000}
              />
          </div>
      </div>
  );

  const renderTools = () => {
    if (activeTool) {
        return (
            <div className="animate-fade-in h-full flex flex-col">
                <button onClick={() => setActiveTool(null)} className="mb-6 text-slate-400 hover:text-white flex items-center font-medium transition-colors">
                    <ChevronLeft className="w-5 h-5 mr-1" /> Zurück zur Übersicht
                </button>
                <div className="flex-1 bg-slate-900/50 border border-slate-700/50 rounded-3xl p-6 overflow-y-auto backdrop-blur-sm">
                    {activeTool === 'kanzlei' && <KanzleiBotDemo />}
                    {activeTool === 'roi' && <ROICalculator />}
                    {activeTool === 'playbook' && <SalesPlaybook />}
                </div>
            </div>
        );
    }
    return (
        <div className="space-y-6 animate-fade-in">
            <h3 className="text-xl font-bold text-white mb-6">Sales Tools</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                
                {/* TOOL CARD 1 */}
                <div onClick={() => setActiveTool('kanzlei')} className="bg-slate-900 p-8 rounded-3xl cursor-pointer border border-slate-800 hover:border-blue-500/50 hover:bg-slate-800 transition-all group relative overflow-hidden">
                    <div className="absolute top-0 right-0 bg-blue-600 text-white text-[10px] font-bold px-3 py-1 rounded-bl-xl">POPULAR</div>
                    <div className="w-14 h-14 bg-blue-900/20 text-blue-400 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                        <FileText className="w-7 h-7" />
                    </div>
                    <h3 className="text-xl font-bold text-white mb-2">Bot Live-Demo</h3>
                    <p className="text-slate-400 text-sm mb-4">Zeigen Sie dem Klienten live, wie ein Diktat in ein perfektes PDF verwandelt wird.</p>
                    <div className="text-blue-400 text-xs font-bold flex items-center mt-auto">
                        Tool öffnen <ArrowRight className="w-3 h-3 ml-1 group-hover:translate-x-1 transition-transform" />
                    </div>
                </div>

                {/* TOOL CARD 2 */}
                <div onClick={() => setActiveTool('roi')} className="bg-slate-900 p-8 rounded-3xl cursor-pointer border border-slate-800 hover:border-purple-500/50 hover:bg-slate-800 transition-all group">
                    <div className="w-14 h-14 bg-purple-900/20 text-purple-400 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                        <Calculator className="w-7 h-7" />
                    </div>
                    <h3 className="text-xl font-bold text-white mb-2">Ersparnis Rechner</h3>
                    <p className="text-slate-400 text-sm mb-4">Rechnen Sie dem Anwalt vor, wie viel Zeit er durch automatisierte Aktenvermerke spart.</p>
                    <div className="text-purple-400 text-xs font-bold flex items-center mt-auto">
                        Tool öffnen <ArrowRight className="w-3 h-3 ml-1 group-hover:translate-x-1 transition-transform" />
                    </div>
                </div>

                {/* TOOL CARD 3 */}
                <div onClick={() => setActiveTool('playbook')} className="bg-slate-900 p-8 rounded-3xl cursor-pointer border border-slate-800 hover:border-amber-500/50 hover:bg-slate-800 transition-all group">
                    <div className="w-14 h-14 bg-amber-900/20 text-amber-400 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                        <Presentation className="w-7 h-7" />
                    </div>
                    <h3 className="text-xl font-bold text-white mb-2">Pitch & Scripts</h3>
                    <p className="text-slate-400 text-sm mb-4">Argumentationshilfen für "Sicherheit", "Frankfurt Server" und "Nextcloud".</p>
                    <div className="text-amber-400 text-xs font-bold flex items-center mt-auto">
                        Tool öffnen <ArrowRight className="w-3 h-3 ml-1 group-hover:translate-x-1 transition-transform" />
                    </div>
                </div>
            </div>
        </div>
    );
  };

  return (
    <div className="animate-fade-in h-full flex flex-col">
      <header className="flex justify-between items-center mb-8 border-b border-white/5 pb-6">
        <div>
            <h2 className="text-3xl font-bold text-white tracking-tight">Partner Portal</h2>
            <p className="text-slate-400 text-sm mt-1">ID: <span className="font-mono text-amber-400">{partnerId}</span></p>
        </div>
        <div className="flex bg-slate-900 p-1 rounded-xl border border-slate-800">
            <button onClick={() => setActiveTab('dashboard')} className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${activeTab === 'dashboard' ? 'bg-blue-600 text-white shadow-lg' : 'text-slate-400 hover:text-white'}`}>Overview</button>
            <button onClick={() => setActiveTab('tools')} className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${activeTab === 'tools' ? 'bg-blue-600 text-white shadow-lg' : 'text-slate-400 hover:text-white'}`}>Sales Tools</button>
        </div>
      </header>
      <div className="flex-1 overflow-y-auto pr-2 custom-scrollbar">
          {activeTab === 'dashboard' ? renderDashboard() : renderTools()}
      </div>
    </div>
  );
};

export default AffiliatePortal;
